import InputField from 'components/fields/InputField';
import LeadrsLogo from 'components/icons/LeadrsLogo';
import Default from 'layouts/auth/types/Default';
import Checkbox from 'components/checkbox';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { GoogleLogin } from '@react-oauth/google';
import { saveReferralCode, getReferralCode } from 'utils/referral';

function SignUpDefault() {
    const [error, setError] = useState('');
    const [newUser, setNewUser] = useState({
        name: '',
        surname: '',
        email: '',
        password: '',
        phone: '',
        siret: '',
        referal: '',
        company: '',
        type: 'buyer',
        role: 'ceo',
        accept_tos: false,
    });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        if (ref) {
            saveReferralCode(ref);
            setNewUser((prev) => ({ ...prev, referal: ref }));
            return;
        }

        const storedRef = getReferralCode();
        if (storedRef) setNewUser((prev) => ({ ...prev, referal: storedRef }));
    }, []);

    const loginGoogle = (token: string) => {
        if (!newUser.phone.trim()) {
            setError('Le numéro de téléphone est obligatoire');
            return;
        }
        axios
            .post(`${CONFIG.apiUrl}/auth/google`, { ...newUser, token })
            .then((response: any) => {
                if (response.data.token) {
                    localStorage.setItem('token', response.data.token);
                    localStorage.setItem('user-type', response.data.type);
                    window.location.href = '/dashboard';
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    };

    const handleChange = (e: any) => {
        const { id, value } = e.target;

        if (id === 'accept_tos') {
            setNewUser((prevState) => ({
                ...prevState,
                [id]: !prevState.accept_tos,
            }));
            return;
        }
        setNewUser((prevState) => ({
            ...prevState,
            [id]: value,
        }));
        setError('');
    };

    const handleSubmit = (e: any) => {
        if (!newUser.phone.trim()) {
            setError('Le numéro de téléphone est obligatoire');
            return;
        }
        axios
            .post(`${CONFIG.apiUrl}/auth/register`, newUser)
            .then((res: any) => {
                localStorage.setItem('user-type', newUser.type);
                localStorage.setItem('token', res.data.token);
                if (res.data && res.data.token.length > 10)
                    window.location.href = '/dashboard';
            })
            .catch((err: any) => {
                setError(err.response.data.error);
            });
    };

    return (
        <Default
            maincard={
                <div className="flex min-h-full w-full items-center justify-center px-6 py-12 lg:items-start lg:justify-start lg:px-12 lg:pt-[8vh]">
                    <div className="w-full max-w-[400px] flex-col">
                        {/* Heading */}
                        <h1 className="mt-10 font-crimson text-[42px] leading-[1.1] text-leadrs-text">
                            Rejoindre Leadrs
                        </h1>
                        <p className="mt-3 font-geist text-base text-leadrs-text-soft">
                            Achat de leads qualifiés à la demande.
                        </p>

                        {/* Error */}
                        {error && (
                            <div className="mt-4 rounded-lg bg-leadrs-red-soft px-4 py-3">
                                <p className="font-geist text-sm text-leadrs-red-focus">
                                    {error}
                                </p>
                            </div>
                        )}

                        {/* Fields */}
                        <div className="mt-6 space-y-3">
                            <InputField
                                variant="auth"
                                label="Email*"
                                placeholder="contact@leadrs.co"
                                id="email"
                                type="email"
                                onChange={handleChange}
                            />
                            <InputField
                                variant="auth"
                                label="Mot de passe"
                                placeholder="········"
                                id="password"
                                type="password"
                                onChange={handleChange}
                            />
                            <InputField
                                variant="auth"
                                label="Numéro de téléphone*"
                                placeholder="+33 6 00 00 00 00"
                                id="phone"
                                type="tel"
                                onChange={handleChange}
                            />
                            <InputField
                                variant="auth"
                                label="Code de parrainage"
                                placeholder="RF57834"
                                id="referal"
                                type="text"
                                value={newUser.referal}
                                onChange={handleChange}
                            />
                        </div>

                        {/* TOS */}
                        <div className="mt-5 flex items-start gap-2 px-1">
                            <Checkbox
                                id="accept_tos"
                                color="leadrs-yellow"
                                onClick={handleChange}
                            />
                            <label
                                htmlFor="accept_tos"
                                className="font-geist text-sm leading-relaxed text-leadrs-text-soft"
                            >
                                J'ai lu et j'accepte les{' '}
                                <a
                                    href="/terms"
                                    className="text-leadrs-yellow-focus underline"
                                >
                                    Conditions Générales d'Utilisation
                                </a>
                            </label>
                        </div>

                        {/* Submit */}
                        <button
                            className="mt-5 w-full rounded-md bg-leadrs-yellow-focus py-3 font-geist text-base font-medium text-white transition-colors hover:bg-leadrs-yellow-focus/80"
                            onClick={handleSubmit}
                        >
                            M'inscrire
                        </button>

                        {/* Divider */}
                        <div className="mt-5 flex items-center gap-3">
                            <div className="h-px w-full bg-leadrs-stroke" />
                            <span className="font-geist text-xs text-leadrs-text-soft">
                                OU
                            </span>
                            <div className="h-px w-full bg-leadrs-stroke" />
                        </div>

                        {/* Google */}
                        <div className="mt-4 flex w-full">
                            <GoogleLogin
                                onSuccess={(credentialResponse: any) => {
                                    loginGoogle(credentialResponse.credential);
                                }}
                                containerProps={{ className: 'w-full' }}
                                shape="circle"
                                size="large"
                                theme="outline"
                                logo_alignment="center"
                                onError={() => {
                                    console.log('Login Failed');
                                }}
                            />
                        </div>

                        {/* Sign in link */}
                        <p className="mt-5 font-geist text-sm text-leadrs-text-soft">
                            Déjà inscrit ?{' '}
                            <a
                                href="/auth/sign-in/"
                                className="font-medium text-leadrs-yellow-focus underline-offset-2 hover:underline"
                            >
                                Se connecter
                            </a>
                        </p>
                    </div>
                </div>
            }
        />
    );
}

export default SignUpDefault;
